import { Dialog, DialogActions, DialogBody, DialogTitle, ShowProps } from '@/components/base/dialog'
// import { Divider } from '@/components/base/divider'
import { useContext, useEffect, useState } from 'react'
import { PackageQuery } from './__generated__/show_query'
import { Query } from './show_query'
import { useQuery } from '@apollo/client'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Subheading } from '@/components/base/heading'
import { BanknotesIcon, CreditCardIcon } from '@heroicons/react/24/solid'
import { Accordion } from '@/components/accordion'
import { AttachmentResourceType, CustomFieldResource, DocumentSubjectType } from '@/types'
import { Table as CustomFieldValueTable } from '@/components/custom_field_value/table'
import { Carousel } from '../carousel'
import { CurrentTenantContext } from '@/core/current_tenant_context'
import { formatCurrency } from '@/helpers/formatCurrency'
import { Text } from '@/components/base/text'
import { Badge as CategoryBadge } from '@/components/category/badge'
import { Badge } from '../base/badge'
import { Table as ProductSpecificationTable } from '@/components/product_specification/table'
import { Table as MultipliersTable } from '@/components/product_multiplier_type/table'
import { Table as EventTable } from '@/components/event/table'
import { Table as DocumentsTable } from '@/components/document/table'
import { EditDialog } from './edit_dialog'
import { Button } from '../base/button'
import { Collection as AttachmentsCollection } from '@/components/attachment/collection'

type Package = PackageQuery['packages']['nodes'][0]

export const ShowDialog = ({ id, open, setOpen }: ShowProps) => {
  const [openEdit, setOpenEdit] = useState(false)
  const [pakage, setPackage] = useState<Package | null>(null)
  const { data, loading, error } = useQuery<PackageQuery>(Query, { variables: { filters: { idEq: id } } })
  const { currentCompany } = useContext(CurrentTenantContext)

  useEffect(() => {
    if (!data || !id) { return }

    setPackage(data.packages.nodes[0])
  }, [id, data])

  const onEdit = () => {
    setOpenEdit(true)
  }

  return (
    <>
      <Dialog open={open} size="7xl" onClose={setOpen}>
        <DialogTitle>
          {
            loading || !pakage
              ? <Loading />
              : <div className="flex flex-row justify-between">
                <div>
                  <span className="flex flex-row gap-4 items-center pb-6">
                    { pakage.name }
                    <div className="flex flex-wrap gap-2">
                      { pakage.categories.map(c => <CategoryBadge key={c.id} category={c} />) }
                      <Badge color={pakage.availableToEvents ? 'green' : 'red'}>
                        { pakage.availableToEvents ? 'Available to events' : 'Unavailable to events' }
                      </Badge>
                      <Badge color={pakage.displayOnWebsite ? 'green' : 'red'}>
                        { pakage.displayOnWebsite ? 'Display on website' : 'Hide from website' }
                      </Badge>
                    </div>
                  </span>
                  <span className="flex flex-row gap-2 items-center">
                    <BanknotesIcon className="h-6 w-6" />
                    <Subheading>Cost</Subheading>
                    <Text>{ formatCurrency(currentCompany?.currency || 'GBP', pakage.costCents) }</Text>
                  </span>
                  <span className="flex flex-row gap-2 items-center">
                    <CreditCardIcon className="h-6 w-6" />
                    <Subheading>Base Rate</Subheading>
                    <Text>{ formatCurrency(currentCompany?.currency || 'GBP', pakage.rateCents) }</Text>
                  </span>
                  { pakage.description && (
                    <span className="flex flex-row gap-2 items-center pt-6">
                      <Text>{pakage.description}</Text>
                    </span>
                  )}
                </div>
              </div>
          }
        </DialogTitle>
        <DialogBody>
          { error && <Error message={error.message} />}
          { pakage && <div className="flex flex-col gap-4">
            <Carousel images={pakage.images} />
            {/* <Text>TODO: Calendar goes here</Text>
            <Divider /> */}
            <Accordion title="Specifications">
              <ProductSpecificationTable filters={{ productIdEq: pakage.id }} fixedHeight />
            </Accordion>
            <Accordion title="Multipliers">
              <MultipliersTable filters={{ productIdEq: pakage.id }} fixedHeight />
            </Accordion>
            <Accordion title="Stats">
              <div>
                <p>Package cost: { formatCurrency('gbp', pakage.costCents) }</p>
                <p>Revenue: { formatCurrency('gbp', pakage.revenueCents) }</p>
                <p>Return on investment: { pakage.returnOnInvestment }%</p>
              </div>
            </Accordion>
            <Accordion title="Attachments">
              <AttachmentsCollection
                filters={{ nameEq: 'attachments', resourceIdEq: pakage.id, resourceTypeEq: AttachmentResourceType.Package }}
                pageSize={10}
                fixedHeight
              />
            </Accordion>
            <Accordion title="Documents">
              <DocumentsTable filters={{ subjectIdEq: pakage.id, subjectTypeEq: DocumentSubjectType.Package }} pageSize={20} fixedHeight />
            </Accordion>
            <Accordion title="Custom fields">
              <CustomFieldValueTable
                filters={{
                  resourceIdEq: pakage.id,
                  resourceTypeEq: CustomFieldResource.Package
                }}
                fixedHeight
              />
            </Accordion>
            <Accordion title="Event History">
              <EventTable
                filters={{
                  eventItemsItemIdEq: pakage.id,
                }}
                fixedHeight
              />
            </Accordion>
          </div> }
        </DialogBody>
        <DialogActions>
          { pakage && <Button color="amber" onClick={onEdit}>Edit</Button> }
        </DialogActions>
      </Dialog>
      { pakage && <EditDialog id={pakage.id} open={openEdit} setOpen={setOpenEdit} /> }
    </>
  )
}
